import Vue from 'vue'
import { EventBus, EVENTS } from '@/services/event-bus'
import { isEmpty as isObjectEmpty } from '@/helpers/object'
import { removeFromArray } from '@/helpers/array'
import { capitalizeFirstLetter } from '@/helpers/string'

export const OVERLAY_IDS = {
  countryChange: 'country-change',
  cyclonNavigation: 'cyclon-navigation',
  exploreFilter: 'explore-filter',
  exploreSort: 'explore-sort',
  dynamic: 'dynamic',
  leaveReview: 'leave-review',
  outOfStock: 'out-of-stock',
  quickView: 'quick-view',
  restrictedZipCode: 'restricted-zip-code',
  shoeMatcher: 'shoe-matcher',
  sizeGuide: 'size-guide',
}

export const OVERLAY_COMPONENT_NAMES = {
  [OVERLAY_IDS.countryChange]: 'OverlayCountryChange',
  [OVERLAY_IDS.cyclonNavigation]: 'OverlayCyclonNavigation',
  [OVERLAY_IDS.exploreFilter]: 'OverlayExploreFilter',
  [OVERLAY_IDS.dynamic]: 'OverlayDynamic',
  [OVERLAY_IDS.exploreSort]: 'OverlayExploreSort',
  [OVERLAY_IDS.leaveReview]: 'OverlayLeaveReview',
  [OVERLAY_IDS.outOfStock]: 'OverlayOutOfStock',
  [OVERLAY_IDS.quickView]: 'OverlayQuickView',
  [OVERLAY_IDS.restrictedZipCode]: 'OverlayRestrictedZipCode',
  [OVERLAY_IDS.shoeMatcher]: 'OverlayShoeMatcher',
  [OVERLAY_IDS.sizeGuide]: 'OverlaySizeGuide',
}

export const URL_PARAMS_TO_SHOW_OVERLAYS = {
  [OVERLAY_IDS.shoeMatcher]: 'shoe_matcher',
}

export const isValidId = value => Object.values(OVERLAY_IDS).includes(value)

// A show, hide, and preload action are auto-generated for each OVERLAY_IDS
// i.e. OVERLAY_IDS.sizeGuide will have showSizeGuideOverlay(props) and preloadSizeGuideOverlay()
const AUTO_GENERATED_ACTIONS = {
  show: 'show',
  hide: 'hide',
  preload: 'preload',
}

function getActionFunction(action, id) {
  const functions = {
    [AUTO_GENERATED_ACTIONS.show]: function ({ dispatch }, props = {}) {
      dispatch('show', { id, props })
    },
    [AUTO_GENERATED_ACTIONS.hide]: function ({ dispatch }) {
      dispatch('hide', { id })
    },
    [AUTO_GENERATED_ACTIONS.preload]: function ({ dispatch }) {
      dispatch('preload', { id })
    },
  }
  return functions[action]
}

function generateActionForEachOverlayIds(store) {
  Object.values(AUTO_GENERATED_ACTIONS).forEach(action => {
    for (const key in OVERLAY_IDS) {
      const actionName = `${action}${capitalizeFirstLetter(key)}Overlay`
      store.actions[actionName] = getActionFunction(action, OVERLAY_IDS[key])
    }
  })
}

function getIdFromPayload(payload) {
  return payload.id || payload
}

const storeSettings = {
  namespaced: true,
  state: {
    components: {},
    props: {},
    visibleOverlayIds: [],
    renderedOverlayIds: [],
  },

  mutations: {
    addComponent(state, { id }) {
      if (state.components[id] || !isValidId(id) || !OVERLAY_COMPONENT_NAMES[id]) return

      const component = () => {
        const componentName = OVERLAY_COMPONENT_NAMES[id]

        return import(
          /* webpackChunkName: "[request]" */ `@/components/_shared/Overlay/${componentName}/${componentName}.vue`
        )
      }
      Vue.set(state.components, id, component)
    },

    setProps(state, payload) {
      if (isValidId(payload.id) && !isObjectEmpty(payload.props)) {
        Vue.set(state.props, payload.id, payload.props)
      }
    },

    setOverlayRenderedState(state, payload) {
      if (!isValidId(payload.id)) return

      // If the targeted overlay is already rendered, remove it from the renderedOverlayIds
      // and push it back into the array to have it at the end which means it is the one
      // visible on top of all the visible overlays.
      removeFromArray(state.renderedOverlayIds, payload.id)
      if (payload.isRendered) state.renderedOverlayIds.push(payload.id)
      else EventBus.$emit(EVENTS.overlay.close, payload.id)
    },
  },

  actions: {
    /**
     * Show an overlay based on its id.
     *
     * @name show
     * @function
     * @param {object} context - store context
     * @param {string|object} payload - the id itself or an object containing the id and props
     * @param {string} payload.id - a valid overlay id
     * @param {object} payload.props - an object setting the value of the props (optional)
     */
    show({ commit, dispatch }, payload) {
      const id = getIdFromPayload(payload)
      if (payload.props) commit('setProps', payload)

      commit('addComponent', { id })
      commit('setOverlayRenderedState', { id, isRendered: true })
      dispatch('setOverlayVisibility', { id, isVisible: true })
    },

    hide({ dispatch }, payload) {
      const id = getIdFromPayload(payload)
      dispatch('setOverlayVisibility', { id, isVisible: false })
    },

    preload({ state, commit }, payload) {
      const id = getIdFromPayload(payload)

      commit('addComponent', { id })

      // make the browser download the scripts (doesn't render the component)
      return state.components[id]()
    },

    setOverlayVisibility({ state, commit }, payload) {
      if (!isValidId(payload.id)) return

      removeFromArray(state.visibleOverlayIds, payload.id)

      if (payload.isVisible) state.visibleOverlayIds.push(payload.id)
      else commit('setOverlayRenderedState', { id: payload.id, isRendered: false })
    },
  },

  getters: {
    renderedComponents: state => {
      return state.renderedOverlayIds.map(id => {
        return { is: state.components[id], id, props: state.props[id] }
      })
    },
    hasVisibleOverlays: state => !!state.visibleOverlayIds.length,
  },
}
generateActionForEachOverlayIds(storeSettings)

export default storeSettings
